const selectors = {
  root: '[data-ofix-product-details]',
  source: '[data-ofix-description-source]',
};

function headingName(element) {
  return element?.textContent?.trim().toLowerCase() || '';
}

function sectionAfterHeading(container, pattern) {
  const heading = [...container.querySelectorAll('h2,h3,h4,h5,h6')].find((node) => pattern.test(headingName(node)));
  if (!heading) return [];

  const content = [];
  const headingLevel = Number(heading.tagName.slice(1));
  let node = heading.nextElementSibling;
  while (node) {
    const nodeLevel = /^H[2-6]$/.test(node.tagName) ? Number(node.tagName.slice(1)) : 0;
    if (nodeLevel && nodeLevel <= headingLevel) break;
    content.push(node.cloneNode(true));
    node = node.nextElementSibling;
  }
  return content;
}

function listItems(nodes) {
  return nodes.flatMap((node) => node.matches?.('li') ? [node] : [...node.querySelectorAll?.('li') || []]);
}

function cleanFeature(item) {
  const clone = item.cloneNode(true);
  return clone.innerHTML.trim();
}

function benefitParts(item) {
  const strong = item.querySelector('strong');
  const title = strong?.textContent?.replace(/:$/, '').trim() || item.textContent.trim().split(':')[0];
  const clone = item.cloneNode(true);
  clone.querySelector('strong')?.remove();
  const text = clone.textContent.replace(/^\s*:\s*/, '').trim();
  return { title, text };
}

function benefitIcon(title, index) {
  const normalized = title.toLowerCase();
  const icons = {
    design: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 1.25 3.75L17 8l-3.75 1.25L12 13l-1.25-3.75L7 8l3.75-1.25L12 3Z"/><path d="m18.5 13 .75 2.25L21.5 16l-2.25.75L18.5 19l-.75-2.25L15.5 16l2.25-.75L18.5 13Z"/><path d="M5.5 12 6.3 14.2l2.2.8-2.2.8L5.5 18l-.8-2.2-2.2-.8 2.2-.8L5.5 12Z"/></svg>',
    spacious: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"/><path d="m3 8 6-6M21 8l-6-6M3 16l6 6M21 16l-6 6"/></svg>',
    storage: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3h16v18H4z"/><path d="M4 9h16M4 15h16M10 6h4M10 12h4M10 18h4"/></svg>',
    cable: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3v5a4 4 0 0 0 4 4h1a4 4 0 0 1 4 4v5"/><path d="M5 3h6M14 21h6M6 8h4"/></svg>',
    durable: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 20 6v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6l8-3Z"/><path d="m8.5 12 2.2 2.2 4.8-5"/></svg>',
    comfort: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 12V7a5 5 0 0 1 10 0v5M5 12h14v6H5zM7 18v3M17 18v3"/></svg>',
  };

  if (/design|modern|style|aesthetic/.test(normalized)) return icons.design;
  if (/spacious|surface|workspace|legroom|space/.test(normalized)) return icons.spacious;
  if (/storage|drawer|shelf|cabinet/.test(normalized)) return icons.storage;
  if (/cable|wire|power/.test(normalized)) return icons.cable;
  if (/durable|construction|strong|steel|frame|sturdy/.test(normalized)) return icons.durable;
  if (/comfort|ergonomic/.test(normalized)) return icons.comfort;
  return [icons.design, icons.spacious, icons.durable][index % 3];
}

function dimensionParts(item) {
  if (item?.label != null && item?.value != null) {
    return { label: item.label, value: normalizeDimensionValue(item.value) };
  }

  const strong = item.querySelector('strong');
  const label = strong?.textContent?.replace(/:$/, '').trim() || item.textContent.trim().split(':')[0];
  const clone = item.cloneNode(true);
  clone.querySelector('strong')?.remove();
  const value = normalizeDimensionValue(clone.textContent.replace(/^\s*:\s*/, '').trim());
  return { label, value };
}

function labelValueFromElement(element) {
  const cells = [...element.querySelectorAll?.(':scope > th, :scope > td') || []];
  if (cells.length >= 2) {
    return {
      label: cells[0].textContent.replace(/:\s*$/, '').trim(),
      value: cells.slice(1).map((cell) => cell.textContent.trim()).filter(Boolean).join(' '),
    };
  }

  const strong = element.querySelector?.('strong, b');
  if (strong) {
    const clone = element.cloneNode(true);
    clone.querySelector('strong, b')?.remove();
    return {
      label: strong.textContent.replace(/:\s*$/, '').trim(),
      value: clone.textContent.replace(/^\s*:\s*/, '').trim(),
    };
  }

  const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';
  const separator = text.indexOf(':');
  if (separator < 1) return null;
  return { label: text.slice(0, separator).trim(), value: text.slice(separator + 1).trim() };
}

function dimensionEntries(node) {
  const candidates = node.matches?.('li, tr, p')
    ? [node]
    : [...node.querySelectorAll?.('li, tr, p') || []];

  return candidates
    .map(labelValueFromElement)
    .filter((entry) => entry?.label && entry?.value);
}

function normalizeDimensionValue(value) {
  const millimeterMatch = value.match(/([\d,.]+)\s*mm\b/i);
  if (!millimeterMatch) return value;

  const millimeters = Number(millimeterMatch[1].replace(/,/g, ''));
  if (!Number.isFinite(millimeters)) return value;

  const accurateInches = (millimeters / 25.4).toFixed(2);
  if (/\([^)]*inch(?:es)?[^)]*\)/i.test(value)) {
    return value.replace(/\([^)]*inch(?:es)?[^)]*\)/i, `(${accurateInches} inches)`);
  }

  return `${value} (${accurateInches} inches)`;
}

function dimensionGroups(nodes) {
  const groups = [];
  let pendingLabel = '';

  nodes.forEach((node) => {
    if (/^H[2-6]$/.test(node.tagName)) {
      pendingLabel = node.textContent?.replace(/\s+/g, ' ').trim() || '';
      return;
    }

    const entries = dimensionEntries(node);
    if (entries.length) {
      if (!pendingLabel && groups.length && node.matches?.('p')) {
        groups.at(-1).entries.push(...entries);
      } else {
        groups.push({ label: pendingLabel, entries });
      }
      pendingLabel = '';
      return;
    }

    const label = node.textContent?.replace(/\s+/g, ' ').trim();
    if (label) pendingLabel = label;
  });

  return groups;
}

function canonicalVariantName(value) {
  return value
    .toLowerCase()
    .replace(/\+/g, ' with ')
    .replace(/&/g, ' and ')
    .replace(/\bonly\b/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function variantPicker(root) {
  const productId = root.dataset.productId;
  return [...document.querySelectorAll('variant-picker[data-template-product-match="true"]')].find(
    (candidate) => !productId || candidate.dataset.productId === productId
  );
}

function selectedVariantValues(root) {
  const picker = variantPicker(root);
  if (!picker) return [];

  return [...picker.querySelectorAll('fieldset')]
    .map((fieldset) => fieldset.querySelector('input:checked')?.value)
    .concat([...picker.querySelectorAll('select')].map((select) => select.value))
    .filter(Boolean);
}

function dimensionGroupLabel(root, group, index) {
  const groupKey = canonicalVariantName(group.label);
  const picker = variantPicker(root);
  const optionValues = picker
    ? [
        ...picker.querySelectorAll('input[value]'),
        ...picker.querySelectorAll('select option[value]'),
      ].map((option) => option.value).filter(Boolean)
    : [];
  const matchingOption = optionValues.find((value) => canonicalVariantName(value) === groupKey);

  if (matchingOption) return matchingOption;
  return group.label.replace(/^only\s+/i, '').trim() || `Option ${index + 1}`;
}

function matchingDimensionGroup(root, groups) {
  if (groups.length <= 1) return { group: groups[0], variantLabel: '' };

  const selectedValues = selectedVariantValues(root);
  for (const selectedValue of selectedValues) {
    const selectedKey = canonicalVariantName(selectedValue);
    const exactMatch = groups.find((group) => canonicalVariantName(group.label) === selectedKey);
    if (exactMatch) return { group: exactMatch, variantLabel: selectedValue };
  }

  for (const selectedValue of selectedValues) {
    const selectedKey = canonicalVariantName(selectedValue);
    const partialMatch = groups.find((group) => {
      const groupKey = canonicalVariantName(group.label);
      return groupKey.length >= 4 && selectedKey.length >= 4 && (groupKey.includes(selectedKey) || selectedKey.includes(groupKey));
    });
    if (partialMatch) return { group: partialMatch, variantLabel: selectedValue };
  }

  return { group: groups[0], variantLabel: groups[0].label };
}

function updateDimensionSelection(root, groups) {
  const list = root.querySelector('[data-ofix-dimensions]');
  const { group } = matchingDimensionGroup(root, groups);
  const selectedIndex = groups.indexOf(group);
  if (!list || selectedIndex < 0) return;

  list.querySelectorAll('[data-ofix-dimension-group]').forEach((card, index) => {
    const selected = index === selectedIndex;
    const badge = card.querySelector('[data-ofix-dimension-selected]');
    card.classList.toggle('is-selected', selected);
    if (selected) card.setAttribute('aria-current', 'true');
    else card.removeAttribute('aria-current');
    if (badge) badge.hidden = !selected;
  });
}

function renderDimensionGroups(root, groups) {
  const list = root.querySelector('[data-ofix-dimensions]');
  if (!list || !groups.length) return;

  list.replaceChildren();
  groups.forEach((group, index) => {
    const card = document.createElement('section');
    const table = document.createElement('dl');
    card.className = 'ofix-product-details__dimension-group';
    card.dataset.ofixDimensionGroup = '';

    if (groups.length > 1) {
      const header = document.createElement('div');
      const heading = document.createElement('h4');
      const badge = document.createElement('span');
      header.className = 'ofix-product-details__dimension-group-heading';
      heading.textContent = dimensionGroupLabel(root, group, index);
      badge.className = 'ofix-product-details__selected-badge';
      badge.dataset.ofixDimensionSelected = '';
      badge.textContent = 'Selected';
      badge.hidden = true;
      header.append(heading, badge);
      card.append(header);
    }

    group.entries.forEach((entry) => {
      const { label, value } = dimensionParts(entry);
      const row = document.createElement('div');
      row.className = 'ofix-product-details__dimension';
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value;
      row.append(term, description);
      table.append(row);
    });

    card.append(table);
    list.append(card);
  });

  updateDimensionSelection(root, groups);

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    list.animate(
      [
        { opacity: 0.4, transform: 'translateY(3px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 160, easing: 'ease-out' }
    );
  }
}

function setupAccordions(root) {
  const accordions = [...root.querySelectorAll('.ofix-product-details__accordion')];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const prepare = (details) => {
    const summary = details.querySelector(':scope > summary');
    const content = [...details.children].filter((child) => child !== summary);
    const panel = document.createElement('div');
    const inner = document.createElement('div');
    panel.className = 'ofix-product-details__accordion-panel';
    inner.className = 'ofix-product-details__accordion-panel-inner';
    inner.append(...content);
    panel.append(inner);
    details.append(panel);
    panel.style.height = details.open ? 'auto' : '0px';
    inner.style.opacity = details.open ? '1' : '0';
    inner.style.transform = details.open ? 'translateY(0)' : 'translateY(-6px)';
    return { details, summary, panel, inner, animation: null };
  };

  const items = accordions.map(prepare);

  const finish = (item, open) => {
    item.animation = null;
    item.panel.style.height = open ? 'auto' : '0px';
    item.inner.style.opacity = open ? '1' : '0';
    item.inner.style.transform = open ? 'translateY(0)' : 'translateY(-6px)';
    if (!open) item.details.removeAttribute('open');
    item.details.classList.remove('is-closing');
  };

  const setOpen = (item, open) => {
    item.animation?.cancel();
    if (reducedMotion) {
      if (open) item.details.setAttribute('open', '');
      finish(item, open);
      return;
    }

    item.details.classList.toggle('is-closing', !open);
    if (open) item.details.setAttribute('open', '');
    const startHeight = item.panel.getBoundingClientRect().height;
    const endHeight = open ? item.inner.scrollHeight : 0;
    item.animation = item.panel.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    item.inner.animate(
      {
        opacity: open ? [0, 1] : [1, 0],
        transform: open ? ['translateY(-6px)', 'translateY(0)'] : ['translateY(0)', 'translateY(-6px)'],
      },
      { duration: open ? 240 : 160, easing: 'ease-out', fill: 'forwards' }
    );
    item.animation.onfinish = () => finish(item, open);
    item.animation.oncancel = () => { item.animation = null; };
  };

  items.forEach((item) => {
    item.summary.addEventListener('click', (event) => {
      event.preventDefault();
      const opening = !item.details.open;
      if (opening) {
        items.filter((other) => other !== item && other.details.open).forEach((other) => setOpen(other, false));
      }
      setOpen(item, opening);
    });
  });
}

function renderProductDetails(root) {
  const template = root.querySelector(selectors.source);
  if (!template) return;

  const source = document.createElement('div');
  source.append(template.content.cloneNode(true));

  const overviewNodes = sectionAfterHeading(source, /overview|description|about/);
  const featureNodes = sectionAfterHeading(source, /key features|features/);
  const dimensionNodes = sectionAfterHeading(source, /dimensions|dimension|size/);
  const materialNodes = sectionAfterHeading(source, /material|construction/);
  const features = listItems(featureNodes);
  const groupedDimensions = dimensionGroups(dimensionNodes);

  const overview = root.querySelector('[data-ofix-overview]');
  if (overviewNodes.length) overview.append(...overviewNodes);
  else {
    const firstParagraph = source.querySelector('p');
    if (firstParagraph) overview.append(firstParagraph.cloneNode(true));
  }

  const benefits = root.querySelector('[data-ofix-benefits]');
  features.slice(0, 3).forEach((item, index) => {
    const { title, text } = benefitParts(item);
    const article = document.createElement('article');
    article.className = 'ofix-product-details__benefit';
    article.innerHTML = `<span class="ofix-product-details__benefit-icon">${benefitIcon(title, index)}</span><div><h3></h3><p></p></div>`;
    article.querySelector('h3').textContent = title;
    article.querySelector('p').textContent = text;
    benefits.append(article);
  });

  const featureList = root.querySelector('[data-ofix-feature-list]');
  features.forEach((item) => {
    const paragraph = document.createElement('p');
    paragraph.className = 'ofix-product-details__feature';
    paragraph.innerHTML = cleanFeature(item);
    featureList.append(paragraph);
  });

  if (groupedDimensions.length) {
    root.ofixDimensionGroups = groupedDimensions;
    renderDimensionGroups(root, groupedDimensions);
  }

  const materials = root.querySelector('[data-ofix-materials]');
  if (materialNodes.some((node) => node.textContent?.trim())) materials.append(...materialNodes);
  else root.querySelector('[data-ofix-materials-row]')?.remove();

  const featureRow = root.querySelector('[data-ofix-features-row]');
  const featurePanel = featureRow?.querySelector('.ofix-product-details__feature-panel');
  const hasDimensions = groupedDimensions.length > 0;
  if (!features.length && !hasDimensions) featureRow?.remove();
  else if (!features.length) {
    featureList.remove();
    featurePanel?.classList.add('ofix-product-details__feature-panel--single');
    const label = featureRow.querySelector('summary > span:first-child');
    if (label) label.textContent = 'Dimensions';
  } else if (!hasDimensions) {
    root.querySelector('[data-ofix-dimensions-wrap]')?.remove();
    featurePanel?.classList.add('ofix-product-details__feature-panel--single');
  }
  if (!benefits.children.length) benefits.remove();

  if (!overview.children.length && !features.length && !hasDimensions && !materialNodes.length) {
    const fallback = root.querySelector('[data-ofix-fallback]');
    fallback.hidden = false;
    fallback.append(...source.cloneNode(true).childNodes);
  }

  setupAccordions(root);
  root.classList.add('is-ready');
}

document.querySelectorAll(selectors.root).forEach(renderProductDetails);

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('variant-picker[data-template-product-match="true"]')) {
    return;
  }

  window.requestAnimationFrame(() => {
    document.querySelectorAll(selectors.root).forEach((root) => {
      if (root.ofixDimensionGroups) updateDimensionSelection(root, root.ofixDimensionGroups);
    });
  });
});

document.addEventListener('variant:update', () => {
  document.querySelectorAll(selectors.root).forEach((root) => {
    if (root.ofixDimensionGroups) updateDimensionSelection(root, root.ofixDimensionGroups);
  });
});
